import copyToClipboard from 'copy-to-clipboard';

import 'fomantic-ui-less/definitions/modules/tab.less';
import '../PageView/styles/markdownRender.css';

import 'fomantic-ui-less/definitions/modules/accordion';
import 'fomantic-ui-less/definitions/modules/popup';
import 'fomantic-ui-less/definitions/modules/tab';

import CheckBoldSvg from '@mdi/svg/svg/check-bold.svg';
import ContentCopySvg from '@mdi/svg/svg/content-copy.svg';

import {friendlyBytes} from './friendlyBytes';
import {imageWrapper} from './imageWrapper';
import {createLogger} from './logger';
import {resolveRichTextUploadUrl} from '../PageView/workflowLaunchAttachments';

const LOGGER = createLogger('RenderedPageComponents');

let markdownEditorPromise;

const findAll = (root, selector) => [
    ...(root.matches?.(selector) ? [root] : []),
    ...(root.querySelectorAll?.(selector) || []),
];

const markInitialized = (element, name) => {
    const attribute = `data-page-view-${name}`;
    if (element.hasAttribute(attribute)) {
        return false;
    }

    element.setAttribute(attribute, 'true');
    return true;
};

const createCodeCopy = () => {
    const copyCodeLink = document.createElement('a');
    copyCodeLink.className = 'right aligned floating ui label basic';
    copyCodeLink.innerHTML = `<img class="icon-12 ui icon" src="${ContentCopySvg}" alt=""> Copy`;
    let linkDisabled;
    copyCodeLink.addEventListener('click', event => {
        event.stopPropagation();
        event.preventDefault();
        if (linkDisabled) {
            return;
        }

        copyCodeLink.innerHTML = `<img class="icon-12 ui icon" src="${CheckBoldSvg}" alt=""> Copied to clipboard`;
        linkDisabled = true;
        setTimeout(() => {
            copyCodeLink.innerHTML = `<img class="icon-12 ui icon" src="${ContentCopySvg}" alt=""> Copy`;
            linkDisabled = false;
        }, 1250);
        copyToClipboard(
            copyCodeLink.parentElement.querySelector('pre code').textContent.trim(),
            {format: 'text/plain'});
        copyCodeLink.focus();
    });
    return copyCodeLink;
};

const loadMarkdownEditor = () => {
    if (!markdownEditorPromise) {
        markdownEditorPromise = Promise.all([
            import(/* webpackChunkName: "markdown-editor" */ '../MarkdownEditor/styles/editor.css'),
            import(/* webpackChunkName: "markdown-editor" */ '../MarkdownEditor'),
        ]).then(([, module]) => module.default);
    }
    return markdownEditorPromise;
};

const createEditor = (richTextTextArea, MarkdownEditor) => {
    const editor = document.createElement('div');
    editor.className = 'ui segments';
    const editorContainer = document.createElement('div');
    editorContainer.className = 'ui segment text-editor';
    const height = richTextTextArea.getBoundingClientRect().height;
    const fileUploadUrl = resolveRichTextUploadUrl(richTextTextArea);
    const clonedRichTextTextArea = richTextTextArea.cloneNode();
    clonedRichTextTextArea.style.display = 'none';
    editorContainer.appendChild(clonedRichTextTextArea);
    let view;
    const createView = (textArea, targetTextArea = textArea) => {
        view && view.destroy();
        view = new MarkdownEditor({
            target: editorContainer,
            doc: textArea.value,
            editable: !textArea.readOnly,
            fileUploadUrl,
            onEditorBlur: markdown => targetTextArea.value = markdown,
        });
        view.height = height;
    };
    clonedRichTextTextArea.addEventListener('refresh-rich-text', () => createView(clonedRichTextTextArea));
    createView(richTextTextArea, clonedRichTextTextArea);
    editor.appendChild(editorContainer);

    if (!richTextTextArea.readOnly) {
        const editorSwitch = document.createElement('div');
        editorSwitch.className = 'ui secondary segment';
        const switchToggle = document.createElement('div');
        switchToggle.className = 'ui compact tiny basic buttons';
        const visualButton = document.createElement('button');
        visualButton.id = `${richTextTextArea.id}Visual`;
        visualButton.className = 'ui button active';
        visualButton.innerText = 'Visual';
        visualButton.addEventListener('click', event => {
            event.preventDefault();
            if (event.target.classList.contains('active')) {
                return;
            }
            event.target.classList.add('active');
            document.getElementById(`${richTextTextArea.id}Markdown`).classList.remove('active');
            const targetTextArea = document.getElementById(richTextTextArea.id);
            createView(targetTextArea);
            targetTextArea.style.display = 'none';
        });
        const markdownButton = document.createElement('button');
        markdownButton.id = `${richTextTextArea.id}Markdown`;
        markdownButton.className = 'ui button';
        markdownButton.innerText = 'Markdown';
        markdownButton.addEventListener('click', event => {
            event.preventDefault();
            if (event.target.classList.contains('active')) {
                return;
            }
            event.target.classList.add('active');
            document.getElementById(`${richTextTextArea.id}Visual`).classList.remove('active');
            const targetTextArea = document.getElementById(richTextTextArea.id);
            targetTextArea.style.height = `${editorContainer.getBoundingClientRect().height - 32}px`;
            targetTextArea.style.display = 'block';
            targetTextArea.focus();
            view && view.destroy();
        });
        switchToggle.appendChild(visualButton);
        switchToggle.appendChild(markdownButton);
        editorSwitch.appendChild(switchToggle);
        editor.appendChild(editorSwitch);
    }
    return editor;
};

const setupRichTextEditors = (root, isExcluded) => {
    const richTextTextAreas = findAll(root, '.markdown-body textarea.rich-text')
        .filter(richTextTextArea => !isExcluded(richTextTextArea))
        .filter(richTextTextArea => markInitialized(richTextTextArea, 'rich-text'));
    if (!richTextTextAreas.length) {
        return;
    }
    loadMarkdownEditor().then(MarkdownEditor => {
        richTextTextAreas.filter(element => element.isConnected)
            .forEach(element => element.replaceWith(createEditor(element, MarkdownEditor)));
    }).catch(error => {
        markdownEditorPromise = undefined;
        richTextTextAreas.filter(element => element.isConnected)
            .forEach(element => element.removeAttribute('data-page-view-rich-text'));
        LOGGER.error('Unable to initialize rich text editor', error);
    });
};

export const setupRenderedPageComponents = (root = document, {setupTabs = true, isExcluded = () => false} = {}) => {
    if (setupTabs) {
        $(findAll(root, '.ui.menu .item[data-tab]')).tab({duration: 0});
    }

    if (findAll(root, '.mermaid').length) {
        import(/* webpackChunkName: "mermaid-renderer" */ 'mermaid').then(module => {
            const mermaid = module.default;
            mermaid.initialize({startOnLoad: false});
            mermaid.run({
                nodes: findAll(root, '.mermaid'),
                postRenderCallback: id => {
                    const image = document.getElementById(id);
                    image.replaceWith(imageWrapper(image));
                },
            });
        });
    }

    if (findAll(root, '.markdown-body .katex, .markdown-body .katex-display').length) {
        import(/* webpackChunkName: "tex-renderer" */ 'katex/dist/katex.css');
    }

    findAll(root, '.markdown-body img:not([class]), .markdown-body svg')
        .filter(image => !image.closest('.ui.image'))
        .forEach(image => image.replaceWith(imageWrapper(image)));

    const codeBlocks = findAll(root, '.markdown-body .code-block');
    if (codeBlocks.length) {
        import(/* webpackChunkName: "highlight-style" */ 'highlight.js/styles/idea.css');
    }
    codeBlocks.forEach(codeBlock => {
        if (!markInitialized(codeBlock, 'code-copy')) {
            return;
        }
        const codeCopy = createCodeCopy();
        if (!codeBlock.querySelector('.ui.right.attached.label')) {
            codeBlock.addEventListener('mouseover',
                () => codeBlock.insertAdjacentElement('beforeend', codeCopy));
        }
        codeBlock.addEventListener('mouseleave', () => {
            if (codeBlock.contains(codeCopy)) {
                codeBlock.removeChild(codeCopy);
            }
        });
    });

    setupRichTextEditors(root, isExcluded);

    findAll(root, '.ui.label.attachment').forEach(attachment => {
        $(attachment).popup({
            variation: 'mini',
            title: attachment.getAttribute('title') || '',
            content: friendlyBytes(attachment.getAttribute('data-size')),
        });
    });

    findAll(root, '.markdown-body .ui.accordion').forEach(element => {
        const title = element.querySelector('.title');
        if (title && !title.querySelector('.expand')) {
            title.insertAdjacentHTML('afterbegin', '<i class="dropdown icon expand"></i>');
        }
        $(element).accordion({
            selector: {trigger: '.title .expand'},
            duration: 0,
            animateChildren: false,
        });
    });
};
